import React, {useEffect, useState} from 'react';
import bn from "../utils/bemnames";
import LoadingSpinner from '../components/LoadingSpinner';
import {fetchBanners, fetchEvent, fetchWidgetData, ICompetitionTournaments, IWidgetResponse} from '../api/match-center';
import {setUpTranslations} from '../utils/translations';
import {getEventIdFromMetas, setUpWidgetMetas} from '../utils/widget-meta';
import {
  baseSportsIndexDomain,
} from "../utils/request";
import {enviromentTypes} from "../utils/helpers";
import {
  BuilderComponentGroupSlug,
  IBannerAdObj,
  IEvent,
  oddsFormats,
} from '../utils/types';
import {applyCustomStyling} from '../utils/helpers';
import {
  setBannerAdInfo,
  setPositionOrder
} from '../utils/match-center';
import {
  getInitialActiveBuilderComponentGroupSlug,
  setAllBuilderComponentStatuses
} from '../utils/menu';
import EventTopBannerFallback from '../components/MatchCenter/EventTopBannerFallback';
import {setUpBuilderComponentGroups} from '../utils/builder-components';
import useCheckContainerWidth from '../utils/useCheckContainerWidth';
import useCheckMobileScreen from "../hooks/useCheckMobileScreen";
import Menu from '../components/Sports/Menu';
import BuilderComponentList from '../components/Sports/BuilderComponentsList';
import {useGlobalState} from "../contexts/GlobalState";
import {fetchPromotedProvider} from "../api/general";
import {MCStyleVariable, setStyleVariable} from "../utils/widget-style";
import {setWidgetProviders} from '../utils/widget-providers';

const bem = bn.create('match-center');

interface IProps {
  eventIdFromJs: number;
  widgetID: number;
  oddsFormat: oddsFormats | null;
  elem: Element;
  isAdmin: boolean;
  hasCache: boolean;
}

const MatchCenter: React.FC<IProps> = (props) => {
  const {
    widgetID,
    elem,
    eventIdFromJs,
    oddsFormat,
    isAdmin = false,
    hasCache
  } = props;
  const [loading, setLoading] = useState<Boolean>(false)
  const [requestFailed, setRequestFailed] = useState<Boolean>(false)
  const [widget, setWidget] = useState<IWidgetResponse | null>(null)
  const [event, setEvent] = useState<IEvent | null>(null)
  const [activeTabSlug, setActiveTabSlug] = useState<BuilderComponentGroupSlug | null>(null)
  const [, setTournaments] = useState<ICompetitionTournaments[]>([])
  const {globalStateDispatch, globalState} = useGlobalState()

  const containerWidth = useCheckContainerWidth(elem);
  const isMobile = useCheckMobileScreen(elem);

  const getWidgetBuilderGroupsForHeader = () => widget?.builderComponentGroup.filter(builderComponentGroup =>
    builderComponentGroup.slug === BuilderComponentGroupSlug.header) ?? []

  const menuTabClicked = (slug: BuilderComponentGroupSlug) => {
    setActiveTabSlug(slug);
  }

  // Fetch widget data, event data, banners
  React.useEffect(() => {
    let isMounted = true;

    const getData = () => {
      setLoading(true)

      try {
        Promise.all([
          // Fetch widget data
          fetchWidgetData(widgetID, eventIdFromJs).then((response: IWidgetResponse) => {
            globalStateDispatch({type: 'setWidget', payload: response})
            globalStateDispatch({type: 'setIfHasCache', payload: hasCache})
            setUpTranslations(response.translations)
            setUpWidgetMetas(response.widgetMetas)
            setAllBuilderComponentStatuses(response.builderComponentGroup)
            setUpBuilderComponentGroups(response.builderComponentGroup)
            setActiveTabSlug(getInitialActiveBuilderComponentGroupSlug(response.builderComponentGroup))
            setTournaments(response.tournaments)
            setWidgetProviders(response.widgetProviders)

            // Fetch Banner Data
            fetchBanners(widgetID).then((bannerAdRes: IBannerAdObj[]) => {
              const widgetWithBannerInfo = setBannerAdInfo(bannerAdRes, response);
              const orderedWidget = setPositionOrder(widgetWithBannerInfo);
              setWidget(orderedWidget);
            });

            // Fetch Event
            fetchEvent(eventIdFromJs ?? getEventIdFromMetas(response.widgetMetas)).then((rEvent: IEvent) => {
              globalStateDispatch({type: 'setEvent', payload: rEvent})
              setEvent(rEvent);
            });
          })

        ]).then(() => {
          setLoading(false)
        })
      } catch (err) {
        console.log(err)
        setRequestFailed(true)
      }
      finally {
        setLoading(false)
      }
    }

    if(isMounted) {
      getData();
    }

    return () => {
      isMounted = false
    }
  }, [hasCache, widgetID, eventIdFromJs, globalStateDispatch])

  useEffect(() => {
    if (eventIdFromJs !== null && globalState.widget !== null) {
      // Apply styling based on widget promoted provider
      let widgetStyles = globalState.widget.widgetStyles
      fetchPromotedProvider({
        widgetId: globalState.widget.id,
        eventId: eventIdFromJs
      }).then((r) => {
        if (r) {
          globalStateDispatch({type: 'setPromotedProvider', payload: r})
          if (r.provider.themeColor && widgetStyles !== undefined) {
            widgetStyles = setStyleVariable(MCStyleVariable.MainThemeColor, r.provider.themeColor, widgetStyles)
          }
        }
      }).catch((error) => {
        console.log(error)
      }).finally(() => {
        if (widgetStyles !== undefined)
          applyCustomStyling(widgetStyles)
      });
    }
  }, [eventIdFromJs, globalState.widget, globalStateDispatch])

  const editSportIndexLink = () => {
    if(process.env.REACT_APP_ENV === enviromentTypes.production && event) {
      return `${baseSportsIndexDomainProduction}/#/tools/channel-importing/event-channels/${event.id}/current-channels`
    }
    if(process.env.REACT_APP_ENV === enviromentTypes.staging && event) {
      return `${baseSportsIndexDomainStaging}/#/tools/channel-importing/event-channels/${event.id}/current-channels`
    }
    return ''
  }

  const baseSportsIndexDomainProduction = baseSportsIndexDomain;
  const baseSportsIndexDomainStaging = 'https://staging.sportsindex.dk';

  if(!hasCache && loading) {
    return (
      <LoadingSpinner/>
    )
  }

  return (
    <>
      {!requestFailed && (
        <div className={`${bem.e('wrapper')} ${(isMobile) ? bem.e('mobile') : ''}`}>
          {event && widget && widget?.builderComponentGroup.length !== 0 ? (
            <>
              {isAdmin && (
                <a
                  className="edit-sport-index-event"
                  target="_blank"
                  rel="noreferrer"
                  href={editSportIndexLink()}
                >
                  Edit SportsIndex Event
                </a>
              )}
              {getWidgetBuilderGroupsForHeader().length > 0 && (
                <>
                  {/* Header section */}
                  <div className={bem.e('header')}>
                    <BuilderComponentList
                      builderComponentGroups={getWidgetBuilderGroupsForHeader()}
                      eventID={event.id}
                      isMobile={isMobile}
                      oddsFormat={oddsFormat ?? null}
                      widget={widget}
                      containerWidth={containerWidth}
                    />
                  </div>
                </>
              )}

              {widget.builderComponentGroup.filter(builderComponentGroup => builderComponentGroup.slug !== BuilderComponentGroupSlug.header).length > 0 && (
                <>
                  {/* Menu section */}
                  <div className={bem.e('menu')}>
                    <Menu
                      widgetBuilderGroups={widget.builderComponentGroup}
                      onGroupTabClick={menuTabClicked}
                    />
                  </div>

                  {/* Main content section */}
                  <div className={bem.e('content')}>
                    <BuilderComponentList
                      builderComponentGroups={widget.builderComponentGroup.filter(builderComponentGroup => (builderComponentGroup.slug !== BuilderComponentGroupSlug.header && builderComponentGroup.slug === activeTabSlug))}
                      eventID={event.id}
                      isMobile={isMobile}
                      oddsFormat={oddsFormat ?? null}
                      widget={widget}
                      containerWidth={containerWidth}
                    />
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              {widget && event && (
                <EventTopBannerFallback
                  isMobile={isMobile}
                />
              )}
            </>
          )}
        </div>
      )}
    </>
  )
}

export default MatchCenter;
